import { useGetLatestMembership } from '#api/member';
import { useScheduleManage } from '#providers/SchedulesManage';
import { useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';

export type ScheduleMembership = {
  lectureName: string;
  lectureId: string;
  completedRoundUntilNow: number;
  totalRound: number;
  confirmationFormId: string;
} | null;

// Recoil atom to manage membership data and selection state
export const $membership = atom<{
  data: ScheduleMembership;
  isSelected: boolean;
}>({
  key: 'scheduleMembership',
  default: {
    data: null,
    isSelected: false
  }
});

const useScheduleMembership = () => {
  const [membership, setMembership] = useRecoilState($membership);

  const { selectedLecture } = useScheduleManage();
  const personId = selectedLecture?.personId;

  // Get latest membership data
  const { data } = useGetLatestMembership(personId);

  useEffect(() => {
    // 1. If no membership is selected and latest membership data exists, update the membership
    if (!membership.isSelected && data) {
      const newMembership = {
        lectureName: data.title || '',
        lectureId: data.lectureId || '',
        completedRoundUntilNow: data.completedRoundUntilNow || 0,
        totalRound: data.totalRound || 0,
        confirmationFormId: data.confirmationFormId || ''
      };

      setMembership(prev => ({
        ...prev,
        data: newMembership
      }));
    }
  }, [data, membership.isSelected, setMembership]);

  // 2. When the user selects a membership, manually update and set isSelected to true
  const updateMembership = (newMembership: ScheduleMembership) => {
    setMembership({
      data: newMembership,
      isSelected: true
    });
  };

  return {
    membership: membership.data, // Return only the data part of the membership
    setMembership: updateMembership
  };
};

export default useScheduleMembership;
